// @ts-strict-ignore
import {ProjectTrackingData} from 'app_api/projects/types';
import {apiClient} from 'common/util/api-client';

import {appApiUrl} from '../utils';

interface ProjectTrackingProps {
  billingCredit: BillingCredit;
  featureGateName: string;
  hasIntegratedArb: boolean;
  hasIntegratedEngage: boolean;
  hasIntegratedSessionReplay: boolean;
  hasScdSync: boolean;
  organization: Organization;
  projectAccount: ProjectAccount;
  tallies: ProjectTallies;
  user: ProjectUser;
  userCount: number;
  workspace?: WorkspaceTrackingProps;
}

interface BillingCredit {
  balance: number;
  ids: number[];
  names: string[];
}

interface Organization {
  id: number;
  name: string;
  userCount: number;
}

interface ProjectAccount {
  arr: number;
  csm: string;
  eventsPlan: BillingPlan;
  eventsPlanArr: number;
  id: number;
  packages: string[];
  peoplePlan: BillingPlan;
  peoplePlanArr: number;
  paidPlanTypes: string;
  servicesArr: number;
}

interface ProjectTallies {
  events: number;
  mtu: number;
  people: number;
}

interface ProjectUser {
  dateJoined: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  role: string;
}

interface BillingPlan {
  cap: number;
  id: number;
  isFree: boolean;
  name: string;
  price: number;
  resourceType: number;
  termLengthInMonths: number;
  tier: string;
}

interface WorkspaceTrackingProps {
  id: number;
  isDefault: boolean;
  isGlobal: boolean;
}

function billingPlanFrom(apiBillingPlan) {
  return {
    ...apiBillingPlan,
    isFree: apiBillingPlan[`is_free`],
    resourceType: apiBillingPlan[`resource_type`],
    termLengthInMonths: apiBillingPlan[`term_length_in_months`],
  };
}

export async function getTrackingData(
  projectId: number | string,
  workspaceId: number | string,
): Promise<ProjectTrackingProps> {
  const url = appApiUrl(`tracking-data`, {projectId, workspaceId});
  const apiResult: ProjectTrackingData = await apiClient.get(url);
  return {
    billingCredit: {
      balance: apiResult.billing_credit_balance,
      ids: apiResult.billing_credit_ids,
      names: apiResult.billing_credit_names,
    },
    featureGateName: apiResult.feature_gate_name,
    hasIntegratedArb: apiResult.has_integrated_arb,
    hasIntegratedEngage: apiResult.has_integrated_engage,
    hasIntegratedSessionReplay: apiResult.has_integrated_session_replay,
    hasScdSync: apiResult.has_scd_sync,
    organization: {
      id: apiResult.organization_id,
      name: apiResult.organization_name,
      userCount: apiResult.organization_user_count,
    },
    projectAccount: {
      arr: apiResult.project_account_arr,
      csm: apiResult.project_account_csm,
      eventsPlan: billingPlanFrom(apiResult.project_account_events_plan),
      eventsPlanArr: apiResult.project_account_events_plan_arr,
      id: apiResult.project_account_id,
      packages: apiResult.project_account_packages,
      paidPlanTypes: apiResult.project_account_paid_plan_types,
      peoplePlan: billingPlanFrom(apiResult.project_account_people_plan),
      peoplePlanArr: apiResult.project_account_people_plan_arr,
      servicesArr: apiResult.project_account_services_arr,
    },
    tallies: {
      events: apiResult.tally_events,
      mtu: apiResult.tally_mtu,
      people: apiResult.tally_people,
    },
    user: {
      dateJoined: apiResult.user.date_joined,
      email: apiResult.user.email,
      id: apiResult.user.id,
      firstName: apiResult.user.first_name,
      lastName: apiResult.user.last_name,
      role: apiResult.user.project_role,
    },
    userCount: apiResult.project_user_count,
    ...(apiResult.workspace && {
      workspace: {
        id: apiResult.workspace.id,
        isDefault: apiResult.workspace.is_default,
        isGlobal: apiResult.workspace.is_global,
      },
    }),
  };
}
